import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, translations } from './translations';

const STORAGE_KEY = 'app.language';

const I18nContext = createContext(null);

const readInitialLanguage = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && translations[stored]) {
      return stored;
    }
  } catch (error) {
    // localStorage może być niedostępny (tryb prywatny) – ignorujemy.
  }
  return DEFAULT_LANGUAGE;
};

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readInitialLanguage);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = useCallback((next) => {
    if (!translations[next]) {
      return;
    }
    setLanguageState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (error) {
      // Brak dostępu do localStorage – zmiana zostaje tylko w pamięci.
    }
  }, []);

  const t = useCallback(
    (key, params) => {
      const dictionary = translations[language] || translations[DEFAULT_LANGUAGE];
      let value = dictionary[key];
      if (value == null) {
        value = translations[DEFAULT_LANGUAGE][key];
      }
      if (value == null) {
        return key;
      }
      if (params) {
        Object.keys(params).forEach((paramName) => {
          value = value.replace(new RegExp(`{{\\s*${paramName}\\s*}}`, 'g'), String(params[paramName]));
        });
      }
      return value;
    },
    [language],
  );

  const contextValue = useMemo(
    () => ({ language, setLanguage, languages: SUPPORTED_LANGUAGES, t }),
    [language, setLanguage, t],
  );

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>;
};

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
};
